import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formSchema = z.object({
  project_type: z.string().min(1, { message: 'Veuillez sélectionner un type de projet' }),
  location: z.string().min(2, { message: 'Veuillez indiquer votre localisation' }),
  surface: z.string().optional(),
  budget_estimate: z.string().optional(),
  desired_deadline: z.string().optional(),
  first_name: z.string().min(2, { message: 'Veuillez indiquer votre prénom' }),
  last_name: z.string().min(2, { message: 'Veuillez indiquer votre nom' }),
  email: z.string().email({ message: 'Veuillez indiquer une adresse email valide' }),
  phone: z.string().min(10, { message: 'Veuillez indiquer un numéro de téléphone valide' }),
  adresse: z.string().min(5, { message: 'Veuillez indiquer votre adresse complète' }),
  npa: z.string().min(4, { message: 'Veuillez indiquer votre code postal' }),
  localite: z.string().min(2, { message: 'Veuillez indiquer votre localité' }),
  societe: z.string().optional(),
  message: z.string().optional(),
});

const Devis = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      project_type: '',
      location: '',
      surface: '',
      budget_estimate: '',
      desired_deadline: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      adresse: '',
      npa: '',
      localite: '',
      societe: '',
      message: '',
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads`, data);
      
      toast.success('Demande envoyée avec succès!', {
        description: 'Redirection vers la page de confirmation...',
        duration: 2000,
      });
      
      // Redirect to thank you page
      setTimeout(() => {
        navigate('/merci');
      }, 1000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Une erreur est survenue', {
        description: 'Veuillez réessayer ou nous contacter directement.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="py-14 sm:py-18 lg:py-24 bg-gradient-to-br from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 space-y-4"
        >
          <h1 
            className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#2B2B2B]"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="quote-page"
          >
            Demander un devis
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto px-4">
            Remplissez ce formulaire pour recevoir un devis personnalisé sous 48h ouvrables.
          </p>
          
          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 pt-4">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#336DBF] flex-shrink-0" />
              <span className="text-xs sm:text-sm text-slate-600 whitespace-nowrap">Réponse sous 48h</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#336DBF] flex-shrink-0" />
              <span className="text-xs sm:text-sm text-slate-600 whitespace-nowrap">Sans engagement</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-[#336DBF] flex-shrink-0" />
              <span className="text-xs sm:text-sm text-slate-600 whitespace-nowrap">Données protégées</span>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="border-slate-200/70 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Votre projet</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="quote-form">
                  {/* Project Type */}
                  <FormField
                    control={form.control}
                    name="project_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type de projet *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="project-type-select">
                              <SelectValue placeholder="Sélectionnez un type de projet" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="renovation_complete">Rénovation complète</SelectItem>
                            <SelectItem value="renovation_partielle">Rénovation partielle</SelectItem>
                            <SelectItem value="carrelage">Carrelage & revêtements</SelectItem>
                            <SelectItem value="sanitaires">Sanitaires</SelectItem>
                            <SelectItem value="plans_3d">Plans 3D / Projection</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Location */}
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Localisation *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Ville / Canton (Suisse romande)" 
                            {...field} 
                            data-testid="location-input"
                          />
                        </FormControl>
                        <FormDescription>
                          Ex: Genève, Lausanne, Montreux, Valais, Fribourg, Neuchâtel
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Surface */}
                  <FormField
                    control={form.control}
                    name="surface"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Surface approximative</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Ex: 8 m²" 
                            {...field} 
                            data-testid="surface-input"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Budget */}
                  <FormField
                    control={form.control}
                    name="budget_estimate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Budget estimatif</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="budget-select">
                              <SelectValue placeholder="Sélectionnez une fourchette" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="moins_10k">Moins de 10’000 CHF</SelectItem>
                            <SelectItem value="10k_20k">10’000 - 20’000 CHF</SelectItem>
                            <SelectItem value="20k_30k">20’000 - 30’000 CHF</SelectItem>
                            <SelectItem value="30k_50k">30’000 - 50’000 CHF</SelectItem>
                            <SelectItem value="plus_50k">Plus de 50’000 CHF</SelectItem>
                            <SelectItem value="a_definir">À définir</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Budget indicatif (vous pourrez l'ajuster après visite)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Deadline */}
                  <FormField
                    control={form.control}
                    name="desired_deadline"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Délai souhaité</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="deadline-select">
                              <SelectValue placeholder="Sélectionnez un délai" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="urgent">Urgent (moins d'1 mois)</SelectItem>
                            <SelectItem value="1_2_mois">1-2 mois</SelectItem>
                            <SelectItem value="2_3_mois">2-3 mois</SelectItem>
                            <SelectItem value="3_6_mois">3-6 mois</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="border-t border-slate-200 pt-6 mt-6">
                    <h3 className="text-lg font-semibold text-[#2B2B2B] mb-4">Vos coordonnées</h3>
                    
                    <div className="space-y-6">
                      {/* First Name */}
                      <FormField
                        control={form.control}
                        name="first_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Prénom *</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Jean" 
                                {...field} 
                                data-testid="first-name-input"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Last Name */}
                      <FormField
                        control={form.control}
                        name="last_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nom *</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Dupont" 
                                {...field} 
                                data-testid="last-name-input"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Email */}
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email *</FormLabel>
                            <FormControl>
                              <Input 
                                type="email"
                                placeholder="jean.dupont@example.com" 
                                {...field} 
                                data-testid="email-input"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Phone */}
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Téléphone *</FormLabel>
                            <FormControl>
                              <Input 
                                type="tel"
                                placeholder="+41 XX XXX XX XX" 
                                {...field} 
                                data-testid="phone-input"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Adresse */}
                      <FormField
                        control={form.control}
                        name="adresse"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Adresse *</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Rue et numéro" 
                                {...field} 
                                data-testid="adresse-input"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* NPA et Localité (côte à côte sur desktop) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <FormField
                          control={form.control}
                          name="npa"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Code postal (NPA) *</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="1200" 
                                  {...field} 
                                  data-testid="npa-input"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="localite"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Localité *</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="Genève" 
                                  {...field} 
                                  data-testid="localite-input"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Société (optionnel) */}
                      <FormField
                        control={form.control}
                        name="societe"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nom de la société (optionnel)</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Votre entreprise" 
                                {...field} 
                                data-testid="societe-input"
                              />
                            </FormControl>
                            <FormDescription>
                              Si vous faites cette demande pour une société
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Message */}
                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message / Détails supplémentaires</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Décrivez votre projet, vos attentes, contraintes spécifiques..."
                                className="min-h-[120px]"
                                {...field}
                                data-testid="message-input"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-6">
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-[#336DBF] hover:bg-[#2F62AA] text-white font-medium py-6 text-base"
                      data-testid="quote-form-submit-button"
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                    </Button>
                    <p className="text-xs text-slate-500 text-center mt-4">
                      En soumettant ce formulaire, vous acceptez d'être contacté concernant votre demande.
                      Vos données sont protégées et ne seront jamais partagées avec des tiers.
                    </p>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center text-sm text-slate-600"
        >
          <p>
            Vous avez des questions? Consultez notre{' '}
            <button onClick={() => navigate('/a-propos')} className="text-[#336DBF] hover:underline font-medium">
              page à propos
            </button>
            {' '}ou explorez nos{' '}
            <button onClick={() => navigate('/galerie')} className="text-[#336DBF] hover:underline font-medium">
              réalisations
            </button>.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Devis;